import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { Button } from "@/shared/ui";
import { CheckIcon } from "@heroicons/react/24/outline";
import { getPlansByUserType, getPlanConfig, type UserType } from "@/shared/config";

export const PricingSection: React.FC = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [selectedUserType, setSelectedUserType] = useState<UserType>("lawyer");

  const userTypes: UserType[] = ["lawyer"];

  const handleSelectPlan = (userType: UserType) => {
    // Navigate to onboarding with pre-selected user type
    navigate("/onboarding/select-type", { state: { preSelectedType: userType } });
  };

  return (
    <section id="pricing" className="py-12 md:py-20 bg-white">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-full min-w-0">
        {/* Header */}
        <div className="text-center mb-12 md:mb-16">
          <h2 className="text-2xl sm:text-3xl md:text-4xl font-display font-bold text-tiber-900 mb-4">
            {t("landing.pricing.title")}
          </h2>
          <p className="text-titan text-base md:text-lg max-w-3xl mx-auto">
            {t("landing.pricing.description")}
          </p>
        </div>

        {/* User Type Tabs */}
        <div className="flex justify-center mb-12">
          <div className="inline-flex bg-romance-100 rounded-xl p-1">
            {userTypes.map((userType) => (
              <button
                key={userType}
                onClick={() => setSelectedUserType(userType)}
                className={`px-6 py-3 rounded-lg font-medium transition-all duration-300 ${
                  selectedUserType === userType
                    ? "bg-white text-juridico shadow-md"
                    : "text-titan hover:text-juridico"
                }`}
              >
                {t(`landing.pricing.userTypes.${userType}`)}
              </button>
            ))}
          </div>
        </div>

        {/* Pricing Cards */}
        <div className="grid md:grid-cols-3 gap-6 lg:gap-8 mb-8 md:mb-12">
          {getPlansByUserType(selectedUserType).map((planType) => {
            const plan = getPlanConfig(planType);
            const isPopular = plan.isPopular;
            const currentLang = i18n.language;
            const planName = currentLang === "es" ? plan.nameEs : plan.name;
            const features = currentLang === "es" ? plan.featuresEs : plan.features;

            return (
              <div
                key={planType}
                className={`relative bg-white rounded-2xl p-6 md:p-8 shadow-lg border-2 transition-all duration-300 hover:shadow-xl ${
                  isPopular
                    ? "border-tiber-500 md:scale-[1.02] lg:scale-[1.05]"
                    : "border-tiber-200 hover:border-tiber-500/50"
                }`}
              >
                {/* Popular Badge */}
                {isPopular && (
                  <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                    <span className="bg-tiber-500 text-white px-4 py-1 rounded-full text-sm font-semibold">
                      {t("landing.pricing.mostPopular")}
                    </span>
                  </div>
                )}

                {/* Plan Header */}
                <div className="text-center mb-6">
                  <h3 className="text-2xl font-bold text-juridico mb-2">
                    {planName}
                  </h3>
                  <div className="mb-3">
                    <span className="text-4xl md:text-5xl font-bold text-juridico">
                      ${plan.price}
                    </span>
                    {plan.price > 0 && (
                      <span className="text-titan text-lg ml-1">
                        /{t("landing.pricing.perMonth")}
                      </span>
                    )}
                  </div>
                </div>

                {/* Features List */}
                <ul className="space-y-3 mb-8">
                  {features.map((feature, index) => (
                    <li key={index} className="flex items-start">
                      <CheckIcon className="w-5 h-5 text-atlantis-600 shrink-0 mt-0.5 mr-3" />
                      <span className="text-titan text-sm leading-relaxed">
                        {feature}
                      </span>
                    </li>
                  ))}
                </ul>

                {/* CTA Button */}
                <Button
                  variant={isPopular ? "primary" : "secondary"}
                  size="lg"
                  className="w-full"
                  onClick={() => handleSelectPlan(selectedUserType)}
                >
                  {plan.price === 0
                    ? t("landing.pricing.getStarted")
                    : t("landing.pricing.selectPlan")}
                </Button>
              </div>
            );
          })}
        </div>

        {/* Note */}
        <div className="text-center">
          <p className="text-titan text-sm md:text-base max-w-3xl mx-auto">
            {t("landing.pricing.note")}
          </p>
        </div>
      </div>
    </section>
  );
};
